import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileSpreadsheet, Loader2, Upload, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("pr_token")}` });
const TEMPLATE = "Item Code,Item Name,Base Qty,UoM,Price,Currency,Supplier Code,From Date,To Date\nNEU-CP,NEUTRAL-CP,1,KGM,79.38,INR,JAINICO,2026-11-01,2027-03-31\n";

const STATUS_CLS = {
  ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
  error: "bg-rose-50 text-rose-700 border-rose-200",
  duplicate: "bg-slate-100 text-slate-500 border-slate-200",
  bom: "bg-amber-50 text-amber-800 border-amber-200",
};

const rowState = (r, exempt) => {
  if (r.status === "ok" && r.not_in_bom && !exempt) return ["bom", "Not in BOM"];
  if (r.status === "ok") return ["ok", r.not_in_bom ? "OK · Consumable" : "OK"];
  if (r.status === "duplicate") return ["duplicate", `Repeat of row ${r.duplicate_of}`];
  return ["error", "Error"];
};

function downloadTemplate() {
  const url = URL.createObjectURL(new Blob([TEMPLATE], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url; a.download = "list_price_upload_template.csv"; a.click();
  URL.revokeObjectURL(url);
}

function PreviewTable({ rows, exempt }) {
  return (
    <div className="max-h-[45vh] overflow-auto border border-slate-200 rounded-sm" data-testid="lpu-preview-table">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 text-left sticky top-0">
          <tr>{["Row", "Item", "Supplier", "Unit", "Price", "Valid", "Status", "Issues"].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const [cls, label] = rowState(r, exempt);
            return (
              <tr key={r.row} className="border-t border-slate-100" data-testid={`lpu-row-${r.row}`}>
                <td className="px-2 py-1 text-slate-400">{r.row}</td>
                <td className="px-2 py-1"><div className="font-mono font-semibold">{r.material_code}</div><div className="text-slate-500 truncate max-w-[180px]">{r.item_name}</div></td>
                <td className="px-2 py-1"><div className="font-mono">{r.supplier_code}</div><div className="text-slate-500 truncate max-w-[160px]">{r.supplier_name || "—"}</div></td>
                <td className="px-2 py-1">{r.unit_code}{r.base_quantity !== 1 && <span className="text-slate-400"> /{r.base_quantity}</span>}</td>
                <td className="px-2 py-1 font-mono text-right whitespace-nowrap">{r.amount} {r.currency_code}</td>
                <td className="px-2 py-1 whitespace-nowrap text-slate-600">{r.valid_from} → {r.valid_to}</td>
                <td className="px-2 py-1"><span className={`px-1.5 py-0.5 rounded-full border text-[10px] whitespace-nowrap ${STATUS_CLS[cls]}`} data-testid={`lpu-status-${r.row}`}>{label}</span></td>
                <td className="px-2 py-1 text-rose-700">{(r.issues || []).join("; ")}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Options({ preview, exempt, setExempt, direct, setDirect, note, setNote }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3 text-xs">
      {preview.summary.not_in_bom > 0 && (
        <label className="flex items-start gap-2 cursor-pointer" data-testid="lpu-exempt-label">
          <input type="checkbox" checked={exempt} onChange={(e) => setExempt(e.target.checked)} data-testid="lpu-exempt-toggle" className="mt-0.5" />
          <span>Exempt {preview.summary.not_in_bom} item(s) not in any BOM as <b>Consumable</b> (otherwise they are skipped)</span>
        </label>
      )}
      <label className="flex items-start gap-2 cursor-pointer">
        <input type="checkbox" checked={direct} onChange={(e) => setDirect(e.target.checked)} data-testid="lpu-direct-toggle" className="mt-0.5" />
        <span>Write straight to SAP — <b>skip L1/L2 approval</b> (marked as admin upload)</span>
      </label>
      <Input className="sm:col-span-2" value={note} onChange={(e) => setNote(e.target.value)} maxLength={200}
        placeholder="Note, e.g. Consumables rate list Nov-2026 (required)" data-testid="lpu-note-input" />
    </div>
  );
}

export function ExcelPriceUpload({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [exempt, setExempt] = useState(true);
  const [direct, setDirect] = useState(false);
  const [note, setNote] = useState("");
  const [result, setResult] = useState(null);

  const reset = () => { setFile(null); setPreview(null); setResult(null); setNote(""); setDirect(false); setExempt(true); };
  const close = () => { reset(); onClose(); };

  const toSend = (preview?.rows || []).filter((r) => r.status === "ok" && (exempt || !r.not_in_bom));

  const check = async () => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/list-prices/upload-preview`, fd, { headers: headers(), timeout: 180000 });
      setPreview(data);
      if (!note) setNote(`Excel upload: ${data.filename}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not check the file");
    } finally { setBusy(false); }
  };

  const commit = async () => {
    if (direct && !window.confirm(`Write ${toSend.length} list price(s) straight to SAP without approval?`)) return;
    setBusy(true);
    try {
      const rows = toSend.map((r) => ({
        supplier_code: r.supplier_code, material_code: r.material_code, amount: r.amount,
        currency_code: r.currency_code, base_quantity: r.base_quantity, unit_code: r.unit_code,
        valid_from: r.valid_from, valid_to: r.valid_to, notes: note,
      }));
      const { data } = await axios.post(`${API}/admin/list-prices/direct-import`,
        { rows, note, direct, exempt_non_bom: exempt }, { headers: headers(), timeout: 300000 });
      setResult(data);
      toast.success(`${data.created.length} list price(s) ${direct ? "sent to SAP" : "sent for approval"}`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-6xl" data-testid="lpu-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileSpreadsheet size={18} className="text-cyan-700" /> Upload supplier rate sheet</DialogTitle></DialogHeader>
        {!preview && (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Columns: Item Code, Item Name, Base Qty, UoM, Price, Currency, Supplier Code, From Date, To Date (.xlsx or .csv, max 300 rows). Nothing is saved until you confirm.</p>
            <div className="flex flex-wrap items-center gap-2">
              <Input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="max-w-sm" data-testid="lpu-file-input" />
              <Button onClick={check} disabled={!file || busy} className="bg-cyan-700 hover:bg-cyan-800" data-testid="lpu-check-btn">
                {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <Upload size={14} className="mr-1" />} Check against SAP
              </Button>
              <Button variant="ghost" size="sm" onClick={downloadTemplate} data-testid="lpu-template-btn"><Download size={14} className="mr-1" /> Template</Button>
            </div>
            {busy && <div className="text-xs text-cyan-700 flex items-center gap-1" data-testid="lpu-checking"><Loader2 size={12} className="animate-spin" /> Checking every row against SAP items, suppliers, units, BOM and purchasing status…</div>}
          </div>
        )}
        {preview && !result && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2 text-xs" data-testid="lpu-summary">
              <span className={`px-2 py-0.5 rounded-full border ${STATUS_CLS.ok}`}>{preview.summary.ok} OK</span>
              <span className={`px-2 py-0.5 rounded-full border ${STATUS_CLS.error}`}>{preview.summary.error} errors (skipped)</span>
              <span className={`px-2 py-0.5 rounded-full border ${STATUS_CLS.duplicate}`}>{preview.summary.duplicate} repeats (skipped)</span>
              <span className={`px-2 py-0.5 rounded-full border ${STATUS_CLS.bom}`}>{preview.summary.not_in_bom} not in BOM</span>
            </div>
            <PreviewTable rows={preview.rows} exempt={exempt} />
            <Options {...{ preview, exempt, setExempt, direct, setDirect, note, setNote }} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={reset} data-testid="lpu-back-btn">Choose another file</Button>
              <Button size="sm" onClick={commit} disabled={busy || !toSend.length || note.trim().length < 5}
                className="bg-cyan-700 hover:bg-cyan-800" data-testid="lpu-commit-btn">
                {busy && <Loader2 size={14} className="animate-spin mr-1" />}
                {direct ? `Write ${toSend.length} to SAP` : `Send ${toSend.length} for approval`}
              </Button>
            </div>
          </div>
        )}
        {result && (
          <div className="space-y-2 text-sm" data-testid="lpu-result">
            <div className="text-emerald-700 font-semibold">{result.created.length} list price(s) created{direct ? " — SAP write running in background (check Pending / History in a few minutes)" : " — waiting for L1 → L2 approval"}.</div>
            {result.failed.length > 0 && (
              <div className="text-rose-700 text-xs" data-testid="lpu-failed">
                {result.failed.length} failed: {result.failed.map((f) => `${f.material_code}/${f.supplier_code}: ${f.error}`).join(" · ")}
              </div>
            )}
            <div className="flex justify-end"><Button size="sm" onClick={close} data-testid="lpu-done-btn">Done</Button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
